/**
 * The page: wires the store to the DOM, runs the chosen source (mock, replay or live), and handles the two
 * approval buttons. Everything it renders comes from the view functions, which escape every value.
 *
 * WP-16: replay runs on a timeline along the bottom (play, pause, scrub, step, 1x to 20x, jump marks), and the
 * same page served at `/r/<runId>` is a read-only shared replay: no modes, no sign-in, no approvals.
 *
 * WP-23: no credential is typed into or held by the page. Live mode asks `GET /api/me` who is signed in (the
 * accounts Worker's session cookie on this host), links to `/sign-in` when nobody is, and shows the repo named by
 * `?org=&repo=`, through the dashboard Worker's same-origin proxy. Approvals record the account's principal.
 */
import { wireCopy } from "@botallion/ui";
import { isCoordinatorError, type Coordinator, type PlatformEvent, type Subscription } from "@botallion/contracts";
import { LADDER_STACK } from "@botallion/contracts/fixtures";
import type { CostReport } from "@botallion/metering";
import { createAuthedClient, type StreamStatus } from "./client.ts";
import { SETTINGS_PATH, SIGN_IN_PATH, coordinatorBase, displayName, loadAccount, repoFromUrl, type AccountState } from "./account.ts";
import { nameOf, type ViewId } from "./describe.ts";
import type { GateNotice } from "./gate.ts";
import { html, type Html } from "./html.ts";
import { startMockRun, type MockRun } from "./mock-run.ts";
import { MARKER_KINDS, Recording, type MarkerKind } from "./recording.ts";
import { parseRecording, ReplayPlayer } from "./replay.ts";
import { loadSharedRun, runIdFromPath, shareLiveRun, type SharedRunMeta } from "./share.ts";
import { stackView, type StackView } from "./stack.ts";
import {
  claudeConnected,
  connectCommand,
  createRepo,
  homeView,
  importHref,
  listRepos,
  loadStarterSpec,
  offerAgent,
  pickRepo,
  planSpec,
  signedOutView,
  startView,
  type AgentOffer,
  type HomeState,
  type PlanSummary,
} from "./start.ts";
import { currentPlan, toSnapshot } from "./state.ts";
import { Store } from "./store.ts";
import { agentsView } from "./views/agents.ts";
import { answersFor, approvalsView, feedView } from "./views/approvals.ts";
import { claimsView } from "./views/claims.ts";
import { clock, panel, type ViewContext } from "./views/common.ts";
import { decisionsView } from "./views/decisions.ts";
import { deploysView } from "./views/deploys.ts";
import { negotiationsView } from "./views/negotiations.ts";
import { planView } from "./views/plan.ts";
import { verdictsView } from "./views/verdicts.ts";
import { controlsView, duration, jumpsView, jumpTarget, marksView, REPLAY_SPEEDS, waitingView, type TimelineMark } from "./views/timeline.ts";
import { designMarkup } from "./design.ts";

type Mode = "mock" | "replay" | "live";
interface Config {
  defaultMode: Mode;
  stackManifestUrl: string | null;
}

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
/** A read-only shared replay (`/r/<runId>`): share.html has no mode switch, no sign-in and no approvals. */
const SHARED = document.body.dataset.shared === "true";
const SPEEDS = [1, 4, 16];
const VIEWS: ViewId[] = ["overview", "negotiations", "plan", "verdicts", "deploys"];
const FOLLOW_DWELL_MS = 3000;

const store = new Store();
/** Who is signed in (WP-23), from `GET /api/me`. */
let account: AccountState = { status: "signed-out" };
/** The repo live mode shows, from `?org=&repo=`. */
let repo = repoFromUrl(location.href);
const me = () => (account.status === "signed-in" ? account.me : null);
const ui = {
  mode: "mock" as Mode,
  view: "overview" as ViewId,
  follow: true,
  lastSwitch: 0,
  speed: 4,
  replaySpeed: 5,
  capGaps: false,
  answers: {} as Record<string, string>,
  busy: new Set<string>(),
  expanded: {} as Record<string, boolean>,
  stack: null as StackView | null,
  /** Live mode only (WP-14): null until the first `GET /v1/costs` answers; undefined in mock and replay. */
  costs: undefined as CostReport | null | undefined,
  waiting: [] as GateNotice[],
  stream: "closed" as StreamStatus,
  mockDone: false,
  recordingName: "three-agent collision (fixture)",
  /** The share link made from the live run, once it is shared. */
  shareUrl: null as string | null,
  sharing: false,
  /** The archived run on a share page. */
  run: null as SharedRunMeta | null,
  /** WP-24: the way in (live mode). The typed spec lives here and in the field, never in the markup. */
  home: { orgs: [], repos: null, error: null, creating: false } as HomeState,
  spec: "",
  starter: false,
  planning: false,
  summary: null as PlanSummary | null,
  startError: null as string | null,
  connected: null as boolean | null,
  newRunOpen: false,
  offers: {} as Record<string, AgentOffer>,
  /** Live design shell: which setup step and which nav section. */
  repoMode: "create" as "create" | "import",
  repoName: "",
  step: "auto" as "auto" | "connect" | "spec",
  section: "runs" as "runs" | "files" | "history" | "agents",
};

let config: Config = { defaultMode: "mock", stackManifestUrl: null };
let mockRun: MockRun | null = null;
let player: ReplayPlayer | null = null;
/** Markers and checkpoints for the recording the player is playing. */
let recording: Recording | null = null;
/** Scrubbing: the slider value being dragged, applied once per frame so a phone keeps up. */
let scrubbing: number | null = null;
let scrubFrame = 0;
let live: { client: Coordinator; sub: Subscription } | null = null;
let rendered: Record<string, string> = {};

// ---------------------------------------------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------------------------------------------

function now(): number {
  if (ui.mode === "replay" && player) return player.recordedNow;
  if (ui.mode === "mock") return store.state.lastTs ? Date.parse(store.state.lastTs) : Date.now();
  return Date.now();
}

function context(): ViewContext {
  const approver =
    ui.mode === "mock"
      ? { handle: "ryan", canApprove: true, why: null }
      : ui.mode === "replay"
        ? { handle: null, canApprove: false, why: SHARED ? null : "A replay is a recording; approve in mock or live mode." }
        : me() && repo
          ? { handle: displayName(me()!), canApprove: true, why: null }
          : { handle: null, canApprove: false, why: me() ? "Choose a repo to approve in." : "Sign in to approve." };
  return { state: store.state, now: now(), approver, answers: ui.answers, busy: ui.busy, expanded: ui.expanded, stack: ui.stack, ...(ui.costs !== undefined ? { costs: ui.costs } : {}) };
}

function mainPanels(ctx: ViewContext): Record<string, Html> {
  const feed = panel("feed", "Activity", feedView(store.latestFeed(14)));
  switch (ui.view) {
    case "overview":
      return {
        a: panel("agents", "Agents", agentsView(ctx)),
        b: panel("claims", "Claims map", claimsView(ctx)),
        c: html`${feed}${panel("latest", "Latest negotiation", negotiationsView(ctx, 1))}`,
      };
    case "negotiations":
      return { a: panel("negotiations", "Negotiation threads", negotiationsView(ctx)), c: feed };
    case "plan":
      return { a: panel("plan", "Work plan", planView(ctx)), c: feed };
    case "verdicts":
      return { a: panel("verdicts", "Verdicts", verdictsView(ctx)), c: feed };
    case "deploys":
      return { a: panel("deploys", "Deploys", deploysView(ctx)), c: feed };
  }
}

/** Set innerHTML only when it changed, so open threads, scroll and focus survive. */
function put(el: HTMLElement, key: string, markup: string): void {
  if (rendered[key] === markup) return;
  rendered[key] = markup;
  el.innerHTML = markup;
}

function renderMain(): void {
  const main = $("main");
  const layout = `layout-${ui.view}`;
  if (main.dataset.layout !== layout) {
    main.dataset.layout = layout;
    main.className = layout;
    main.innerHTML = `<div class="col" id="col-a"></div><div class="col" id="col-b"></div><div class="col" id="col-c"></div>`;
    rendered = Object.fromEntries(Object.entries(rendered).filter(([k]) => !k.startsWith("col-")));
  }
  const panels = mainPanels(context());
  for (const col of ["a", "b", "c"]) put($(`col-${col}`), `col-${col}`, panels[col]?.value ?? "");
}

/** The way in: sign-in, repo picker, or the repo's start panel. Hidden outside live mode. */
function renderStart(): void {
  const el = $("start");
  const on = ui.mode === "live" && !SHARED && account.status !== "unavailable";
  el.hidden = !on;
  if (!on) return;
  let markup: Html;
  if (!me()) markup = signedOutView();
  else if (!repo) markup = homeView(ui.home);
  else {
    const plans = Object.values(store.state.plans);
    const ready = plans.flatMap((p) => currentPlan(store.state, p).packages.filter((w) => w.status === "dispatched").map((w) => ({ specId: p.specId, wpId: w.id, title: w.title })));
    markup = startView({
      org: repo.org,
      repo: repo.repo,
      connected: ui.connected,
      planning: ui.planning,
      summary: ui.summary,
      error: ui.startError,
      starter: ui.starter,
      hasPlan: plans.length > 0,
      newRunOpen: ui.newRunOpen,
      ready,
      offers: ui.offers,
      command: connectCommand(repo.org, repo.repo),
    });
  }
  const active = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
  const focused = active && el.contains(active) ? active.id : null;
  const range = focused && "selectionStart" in active! ? [active!.selectionStart, active!.selectionEnd] : null;
  if (rendered["start"] !== markup.value) {
    rendered["start"] = markup.value;
    el.innerHTML = markup.value;
  }
  const field = el.querySelector<HTMLTextAreaElement>("#spec");
  if (field && field.value !== ui.spec) field.value = ui.spec;
  if (focused) {
    const again = el.querySelector<HTMLInputElement | HTMLTextAreaElement>(`#${CSS.escape(focused)}`);
    if (again && document.activeElement !== again) {
      again.focus();
      if (range) again.setSelectionRange(range[0] ?? null, range[1] ?? null);
    }
  }
}

/** Escalated overlaps: the person decides who lands first. Replays can't decide anything, so they show none. */
function renderDecisions(): void {
  put($("decisions"), "decisions", (ui.mode === "replay" ? html`` : decisionsView(context())).value);
}

function renderApprovals(): void {
  const el = $("approvals");
  const active = document.activeElement as HTMLTextAreaElement | null;
  const key = active?.dataset?.answer;
  const sel = key ? [active!.selectionStart, active!.selectionEnd] : null;
  // A replay shows what waited on a person, with no button or answer field: it can never approve anything.
  put(el, "approvals", (ui.mode === "replay" ? waitingView(store.state) : approvalsView(context())).value);
  if (key && sel) {
    const again = el.querySelector<HTMLTextAreaElement>(`textarea[data-answer="${CSS.escape(key)}"]`);
    if (again && document.activeElement !== again) {
      again.focus();
      again.setSelectionRange(sel[0] ?? null, sel[1] ?? null);
    }
  }
}

function statusText(): Html {
  const s = store.state;
  switch (ui.mode) {
    case "mock": {
      const wait = ui.waiting[0];
      const what = wait ? (wait.kind === "plan" ? "waiting for approval of the work plan" : "waiting for approval to promote") : ui.mockDone ? "run complete" : mockRun?.paused ? "paused" : "running";
      return html`<span class="pill mock">Mock</span> ${what} <span class="muted">${clock(s.lastTs)} | ${s.lastSeq} events</span>`;
    }
    case "replay": {
      if (!player) return html`<span class="pill replay">Replay</span> loading`;
      return html`<span class="pill replay">${SHARED ? "Shared replay" : "Replay"}</span> ${player.playing ? "playing" : player.finished ? "finished" : "paused"} at ${player.speed}x
        <span class="muted">${clock(s.lastTs)} | ${player.applied} of ${player.events.length} events | ${ui.recordingName}</span>`;
    }
    case "live": {
      if (account.status === "unavailable") return html`<span class="pill live off">Live</span> accounts unavailable: ${account.message}`;
      if (!me()) return html`<span class="pill live off">Live</span> sign in to connect`;
      if (!repo) return html`<span class="pill live off">Live</span> choose a repo`;
      const label = { connecting: "connecting", open: "connected", closed: "reconnecting", unauthorised: "not found or session refused" }[ui.stream];
      return html`<span class="pill live ${ui.stream === "open" ? "on" : "off"}">Live</span> ${label} <span class="muted">seq ${s.lastSeq} | ${repo.org}/${repo.repo}</span>`;
    }
  }
}

function renderControls(): void {
  let markup: Html;
  const speedButtons = (current: number, action: string) =>
    html`<span class="group">${SPEEDS.map((x) => html`<button type="button" data-action="${action}" data-speed="${x}" class="${x === current ? "on" : ""}">${x}x</button>`)}</span>`;
  const save = html`<button type="button" data-action="download" title="Save this run's events to replay later">Save log</button>`;
  const toReplay = html`<button type="button" data-action="replay-this" title="Replay what has happened so far">Replay this run</button>`;
  switch (ui.mode) {
    case "mock":
      markup = html`<button type="button" data-action="mock-toggle">${mockRun?.paused ? "Resume" : "Pause"}</button>
        <button type="button" data-action="mock-restart">Restart</button>${speedButtons(ui.speed, "mock-speed")}${save}${toReplay}`;
      break;
    case "replay":
      // The replay controls live on the timeline.
      markup = html``;
      break;
    case "live": {
      const share = me() && repo
        ? html`<button type="button" data-action="share-run" title="Archive this run and make a read-only link anyone can replay"${ui.sharing ? " disabled" : ""}>${ui.sharing ? "Sharing..." : "Share run"}</button>`
        : "";
      const link = ui.shareUrl
        ? html`<span class="sharelink"><input type="text" readonly value="${ui.shareUrl}" aria-label="Read-only replay link" size="28" /><button type="button" data-action="copy-link">Copy</button><a href="${ui.shareUrl}" target="_blank" rel="noopener noreferrer">Open</a></span>`
        : "";
      markup = html`${save}${toReplay}${share}${link}`;
      break;
    }
  }
  const el = document.getElementById("controls");
  if (el) put(el, "controls", markup.value);
}

/** Timeline marks for the current recording, at their (possibly gap-capped) offsets. */
function marks(): TimelineMark[] {
  if (!player || !recording) return [];
  const p = player;
  return recording.markers.map((m) => ({ kind: m.kind, offset: p.offsetAt(m.index), text: m.text }));
}

function renderTimeline(): void {
  const el = $("timeline");
  const on = ui.mode === "replay" && player !== null;
  el.hidden = !on;
  document.body.classList.toggle("replaying", on);
  if (!on || !player) return;
  const ms = marks();
  put($("tl-marks"), "tl-marks", marksView(ms, player.duration).value);
  put($("tl-controls"), "tl-controls", controlsView({ playing: player.playing, finished: player.finished, speed: player.speed, capGaps: ui.capGaps, shared: SHARED, atStart: player.applied === 0 }).value);
  put($("tl-jumps"), "tl-jumps", jumpsView(ms, player.position).value);
}

function renderChrome(): void {
  for (const b of document.querySelectorAll<HTMLButtonElement>("nav.modes button")) b.classList.toggle("on", b.dataset.mode === ui.mode);
  for (const b of document.querySelectorAll<HTMLButtonElement>("nav.tabs button")) b.classList.toggle("on", b.dataset.view === ui.view);
  ($("follow") as HTMLInputElement).checked = ui.follow;
  const signin = document.getElementById("signin");
  if (signin) {
    const who = me();
    (signin.querySelector(".signed-out") as HTMLElement).hidden = who !== null;
    (signin.querySelector(".signed-in") as HTMLElement).hidden = who === null;
    $("who").textContent = who ? displayName(who) : "";
    put($("repo-picker"), "repo-picker", (who && ui.mode === "live" ? repoPicker(who.orgs.map((o) => o.org)) : html``).value);
    signin.classList.toggle("dim", ui.mode !== "live");
  }
  put($("status"), "status", statusText().value);
}

/** The parts of the timeline that move every frame: updated in place so dragging and playback stay smooth. */
function renderProgress(): void {
  if (ui.mode !== "replay" || !player) return;
  const scrub = $<HTMLInputElement>("scrub");
  const total = Math.round(player.duration);
  const pos = scrubbing !== null ? scrubbing : Math.round(player.position);
  if (scrub.max !== String(total)) scrub.max = String(total);
  if (scrubbing === null) scrub.value = String(pos);
  scrub.style.setProperty("--pos", `${total ? (pos / total) * 100 : 0}%`);
  const fine = total < 60_000;
  const elapsed = duration(pos, fine);
  if ($("tl-elapsed").textContent !== elapsed) $("tl-elapsed").textContent = elapsed;
  const length = duration(total, fine);
  if ($("tl-total").textContent !== length) $("tl-total").textContent = length;
  const latest = store.feed.at(-1);
  const caption = latest ? `${clock(latest.ts)}  ${latest.text}` : "Nothing has happened yet.";
  if ($("tl-caption").textContent !== caption) $("tl-caption").textContent = caption;
}

/** While a replay plays, move the timeline every frame rather than once a second. */
let ticking = 0;
function tick(): void {
  if (ticking) return;
  const loop = () => {
    renderProgress();
    ticking = player?.playing ? requestAnimationFrame(loop) : 0;
  };
  ticking = requestAnimationFrame(loop);
}

function renderRunInfo(): void {
  const el = document.getElementById("runinfo");
  const run = ui.run;
  if (!el || !run) return;
  const span = Date.parse(run.lastTs) - Date.parse(run.firstTs);
  const from = run.source.kind === "coordinator" ? `the coordinator's event stream${run.source.repo ? ` for ${run.source.repo}` : ""}` : `a recorded event log${run.source.repo ? ` of ${run.source.repo}` : ""}`;
  put(el, "runinfo", html`<div class="run">
      <strong class="name">${run.name}</strong>
      <span class="muted">Recorded ${run.firstTs.slice(0, 10)} ${clock(run.firstTs)} UTC, ${duration(span)} long, ${run.eventCount} events<span class="from"> from ${from}</span>.</span>
      <span class="muted ro">Read-only: this link can replay the run and nothing else.</span>
      <span class="muted digest" title="SHA-256 of the event log (Save log downloads it)">sha256 ${run.digest.slice(0, 12)}</span>
    </div>`.value);
}

let frame = 0;
function useDesign(): boolean {
  return ui.mode === "live" && !SHARED;
}

function ensureDesignCss(): void {
  if (document.getElementById("design-css")) return;
  const marker = document.createElement("meta");
  marker.id = "design-css";
  document.head.appendChild(marker);
  for (const href of ["/ui/theme.css", "/ui/bundle.css", "/ui/screens.css", "/ui/app.css"]) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
}

function render(): void {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    const design = useDesign();
    document.body.classList.toggle("design", design);
    const mount = document.getElementById("design");
    if (mount) mount.hidden = !design;
    if (design) ensureDesignCss();
    renderChrome();
    renderControls();
    if (design) {
      $("start").hidden = true;
      renderDesign();
      renderDecisions();
      put($("approvals"), "approvals", "");
      put($("main"), "main", "");
      return;
    }
    renderStart();
    renderDecisions();
    renderApprovals();
    renderMain();
    renderTimeline();
    renderProgress();
    if (player?.playing) tick();
  });
}

function renderDesign(): void {
  const el = $("design");
  const who = me();
  const ctx = context();
  const markup = designMarkup({
    me: who ? { name: who.name, email: who.email, orgs: who.orgs } : null,
    unavailable: account.status === "unavailable" ? account.message : null,
    repo,
    home: ui.home,
    repoMode: ui.repoMode,
    repoName: ui.repoName,
    spec: ui.spec,
    planning: ui.planning,
    step: ui.step,
    section: ui.section,
    command: repo ? connectCommand(repo.org, repo.repo) : "",
    sessions: Object.values(store.state.sessions),
    state: store.state,
    answers: ui.answers,
    busy: ui.busy,
    canApprove: ctx.approver.canApprove,
    error: ui.startError,
  });
  const active = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
  const focused = active && el.contains(active) ? active.id || active.dataset.answer || null : null;
  const range = focused && active && "selectionStart" in active ? [active.selectionStart, active.selectionEnd] : null;
  if (rendered["design"] !== markup) {
    rendered["design"] = markup;
    el.innerHTML = markup;
  }
  const spec = el.querySelector<HTMLTextAreaElement>("#spec");
  if (spec && spec.value !== ui.spec) spec.value = ui.spec;
  const name = el.querySelector<HTMLInputElement>("#nm");
  if (name && name.value !== ui.repoName) name.value = ui.repoName;
  for (const area of el.querySelectorAll<HTMLTextAreaElement>("textarea[data-answer]")) {
    const value = ui.answers[area.dataset.answer ?? ""] ?? "";
    if (value && area.value !== value) area.value = value;
  }
  if (!focused || !active) return;
  const again = active.id
    ? el.querySelector<HTMLInputElement | HTMLTextAreaElement>(`#${CSS.escape(active.id)}`)
    : el.querySelector<HTMLTextAreaElement>(`textarea[data-answer="${CSS.escape(active.dataset.answer ?? "")}"]`);
  if (again && document.activeElement !== again) {
    again.focus();
    if (range) again.setSelectionRange(range[0] ?? null, range[1] ?? null);
  }
}

function toast(message: string, tone: "error" | "info" = "error"): void {
  const el = $("toast");
  el.textContent = message;
  el.className = `toast ${tone}`;
  el.hidden = false;
  clearTimeout((toast as unknown as { t?: number }).t);
  (toast as unknown as { t?: number }).t = window.setTimeout(() => (el.hidden = true), 6000);
}

// ---------------------------------------------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------------------------------------------

function stopSources(): void {
  mockRun?.stop();
  mockRun = null;
  player?.stop();
  player = null;
  recording = null;
  scrubbing = null;
  live?.sub.close();
  live = null;
  ui.waiting = [];
  ui.busy.clear();
  ui.stream = "closed";
}

function follow(applied: readonly PlatformEvent[]): void {
  if (!ui.follow || !applied.length) return;
  if (document.activeElement?.closest?.("#approvals")) return;
  const last = store.feed.at(-1);
  if (!last || !applied.some((e) => e.seq === last.seq)) return;
  if (last.view === ui.view || performance.now() - ui.lastSwitch < FOLLOW_DWELL_MS) return;
  ui.view = last.view;
  ui.lastSwitch = performance.now();
}

function startMock(): void {
  ui.mockDone = false;
  ui.stack = stackOrNull(LADDER_STACK);
  mockRun = startMockRun({
    onEvent: (e) => store.apply([e]),
    stepMs: () => 2000 / ui.speed,
    onWaiting: (w) => {
      ui.waiting = w;
      render();
    },
    onDone: (err) => {
      ui.mockDone = true;
      if (err) toast(`The mock run stopped: ${err.message}`);
      render();
    },
  });
}

function startReplay(events: PlatformEvent[], name: string, opts: { at?: number } = {}): void {
  ui.recordingName = name;
  ui.stack = stackOrNull(LADDER_STACK);
  const rec = new Recording(events);
  recording = rec;
  player = new ReplayPlayer(events, {
    speed: ui.replaySpeed,
    capMs: ui.capGaps ? 2000 : null,
    onApply: (evts, reset) => {
      // Backwards, or a long way forwards: rebuild from the nearest checkpoint. Otherwise apply in order.
      const target = reset ? evts.length : store.state.log.length + evts.length;
      if (reset || evts.length > 50) rec.restore(store, target);
      else store.apply(evts);
    },
    onState: render,
  });
  // `?at=<seq>` opens paused on that event: a link to one moment of the run.
  const at = opts.at !== undefined ? events.findIndex((e) => e.seq === opts.at) : -1;
  if (at >= 0) player.seek(player.offsetAt(at));
  else player.play();
}

async function startDefaultReplay(): Promise<void> {
  try {
    const res = await fetch("/recordings/three-agent-collision.json");
    const events = parseRecording(await res.json());
    if (ui.mode === "replay" && !player) startReplay(events, "three-agent collision (fixture)");
  } catch (e) {
    toast(`Could not load the recording: ${(e as Error).message}`);
  }
}

async function loadLiveStack(): Promise<void> {
  ui.stack = null;
  if (!config.stackManifestUrl) return;
  try {
    const res = await fetch(config.stackManifestUrl, { credentials: "omit" });
    const r = stackView(await res.text());
    if (r.ok) ui.stack = r.stack;
    else toast(r.error);
  } catch {
    toast("Could not load the stack manifest.");
  }
  render();
}

/** Live mode: fetch model costs (WP-14). Optional: on any failure the board carries on without them. */
async function refreshCosts(): Promise<void> {
  if (ui.mode !== "live" || !me() || !repo) return;
  try {
    const res = await fetch(`${coordinatorBase(location.origin, repo)}/v1/costs`, { credentials: "same-origin" });
    const body = (await res.json()) as { ok?: boolean; result?: CostReport };
    if (ui.mode === "live" && res.ok && body.ok && body.result) {
      ui.costs = body.result;
      render();
    }
  } catch {
    // Costs are bookkeeping; the board works without them.
  }
}

/** The repo picker in live mode: the account's orgs and a repo name, which become `?org=&repo=`. */
function repoPicker(orgs: string[]): Html {
  if (!orgs.length) return html`<span class="muted">You are not in an org yet.</span> <a href="${SETTINGS_PATH}">Settings</a>`;
  return html`<select id="pick-org" aria-label="Org">${orgs.map((o) => html`<option value="${o}"${repo?.org === o ? " selected" : ""}>${o}</option>`)}</select>
    <input id="pick-repo" type="text" aria-label="Repo" placeholder="repo" size="10" autocomplete="off" spellcheck="false" value="${repo?.repo ?? ""}" />
    <button type="button" data-action="open-repo">Open</button>`;
}

// ---------------------------------------------------------------------------------------------------------------
// The way in (WP-24)
// ---------------------------------------------------------------------------------------------------------------

/**
 * Open a repo. It goes in the URL, so the view is a link; the page keeps nothing in the browser. `starter` marks a
 * repo just made from the demo app, which is the one place the starter spec is offered.
 */
function showRepo(next: { org: string; repo: string }, mode: "replace" | "push" = "replace", starter = false): void {
  const url = new URL(location.href);
  url.searchParams.set("org", next.org);
  url.searchParams.set("repo", next.repo);
  url.searchParams.set("mode", "live");
  if (starter) url.searchParams.set("starter", "1");
  else url.searchParams.delete("starter");
  if (mode === "push") history.pushState(null, "", url);
  else history.replaceState(null, "", url);
  repo = next;
  ui.spec = "";
  ui.starter = false;
  ui.summary = null;
  ui.startError = null;
  ui.offers = {};
  ui.newRunOpen = false;
  switchMode("live");
}

/** Signed in with no repo in the URL: open the only repo there is, else show the list. */
async function loadHome(): Promise<void> {
  const who = me();
  if (!who) return;
  ui.home = { ...ui.home, orgs: who.orgs.map((o) => o.org), repos: null, error: null };
  render();
  const { repos, error } = await listRepos(ui.home.orgs);
  ui.home = { ...ui.home, repos, error };
  const chosen = !repo ? pickRepo(repos) : null;
  if (chosen && ui.mode === "live") return showRepo({ org: chosen.org, repo: chosen.name });
  render();
}

/** What a repo's start panel needs that the event stream doesn't carry. */
async function loadStart(): Promise<void> {
  if (!repo) return;
  const shown = repo;
  ui.connected = null;
  void claudeConnected().then((c) => {
    if (repo === shown) {
      ui.connected = c;
      render();
    }
  });
  if (new URL(location.href).searchParams.get("starter") === "1" && !ui.spec) {
    const text = await loadStarterSpec();
    if (text && repo === shown && !ui.spec) {
      ui.spec = text;
      ui.starter = true;
      render();
    }
  }
}

async function onCreateRepo(template: string): Promise<void> {
  const named = (document.getElementById("nm") as HTMLInputElement | null)?.value ?? ($("new-repo-name") as HTMLInputElement | null)?.value ?? "";
  const name = named.trim();
  ui.repoName = named;
  const org = ($("new-org") as HTMLSelectElement | null)?.value ?? ui.home.orgs[0] ?? "";
  if (!name) {
    ui.home = { ...ui.home, error: "Give the repo a name first, for example ladder." };
    return render();
  }
  if (!repoFromUrl(`${location.origin}/?org=${encodeURIComponent(org)}&repo=${encodeURIComponent(name)}`)) {
    ui.home = { ...ui.home, error: "That isn't a repo name. Use letters, digits, dots, underscores and single hyphens, for example ladder." };
    return render();
  }
  ui.home = { ...ui.home, creating: true, error: null };
  render();
  const made = await createRepo(org, name, template);
  ui.home = { ...ui.home, creating: false, error: made.ok ? null : made.error };
  if (made.ok) {
    return showRepo({ org, repo: name }, "push", template === "demo");
  }
  render();
}

async function onPlanIt(): Promise<void> {
  if (!repo || ui.planning) return;
  const shown = repo;
  ui.step = "auto";
  ui.section = "runs";
  ui.spec = ($("spec") as HTMLTextAreaElement | null)?.value ?? ui.spec;
  ui.planning = true;
  ui.startError = null;
  ui.summary = null;
  render();
  const r = await planSpec(shown.org, shown.repo, ui.spec);
  ui.planning = false;
  if (repo !== shown) return;
  if (r.ok) {
    ui.summary = r.summary;
    ui.newRunOpen = false;
  } else ui.startError = r.error;
  render();
}

async function onOfferAgent(specId: string, wpId: string): Promise<void> {
  if (!repo) return;
  const shown = repo;
  const key = `${specId}/${wpId}`;
  ui.offers[key] = { state: "busy" };
  render();
  const offer = await offerAgent(shown.org, shown.repo, specId, wpId);
  if (repo !== shown) return;
  ui.offers[key] = offer;
  render();
}

/** Show the repo picked in the picker: it goes in the URL, so the view is a link. */
function openRepo(): void {
  const url = new URL(location.href);
  url.searchParams.set("org", $<HTMLSelectElement>("pick-org").value);
  url.searchParams.set("repo", $<HTMLInputElement>("pick-repo").value.trim());
  const next = repoFromUrl(url.toString());
  if (!next) return toast("That is not a repo name: letters, digits, '.', '_' and single hyphens.");
  history.replaceState(null, "", url);
  repo = next;
  switchMode("live");
}

function startLive(): void {
  if (!me() || !repo || live) return;
  const shown = repo;
  // No token: requests go to this origin's proxy for the repo, with the session cookie.
  const client = createAuthedClient({
    baseUrl: coordinatorBase(location.origin, shown),
    onStreamStatus: (s) => {
      ui.stream = s;
      if (s === "unauthorised") {
        toast(`The coordinator refused your session for ${shown.org}/${shown.repo}: signed out, or not a member of that org.`);
        void refreshAccount();
      }
      render();
    },
  });
  // since: 0 replays the whole log first, so the page can also save and replay a live run.
  const sub = client.subscribe({ since: 0 }, (e) => store.apply([e]));
  live = { client, sub };
  void refreshCosts();
  void loadStart();
}

function switchMode(mode: Mode): void {
  stopSources();
  store.reset();
  rendered = {};
  ui.mode = mode;
  ui.expanded = {};
  ui.shareUrl = null;
  ui.costs = mode === "live" ? null : undefined;
  const url = new URL(location.href);
  url.searchParams.set("mode", mode);
  history.replaceState(null, "", url);
  if (mode === "mock") startMock();
  if (mode === "replay") void startDefaultReplay();
  if (mode === "live") {
    void loadLiveStack();
    startLive();
    if (me() && !repo) void loadHome();
  }
  render();
}

function stackOrNull(input: unknown): StackView | null {
  const r = stackView(input);
  return r.ok ? r.stack : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------------------------------------------

/** The coordinator approvals go to: the in-page mock, or the live coordinator as the signed-in account. */
function approvalTarget(): { coordinator: Coordinator; principal: string } | null {
  if (ui.mode === "mock" && mockRun) return { coordinator: mockRun.coordinator, principal: "human:ryan" };
  const who = me();
  if (ui.mode === "live" && live && who) return { coordinator: live.client, principal: who.principal };
  return null;
}

async function approve(key: string, run: (c: Coordinator, principal: string) => Promise<unknown>): Promise<void> {
  const target = approvalTarget();
  if (!target) {
    toast(context().approver.why ?? "Nothing to approve against.");
    return;
  }
  ui.busy.add(key);
  render();
  try {
    await run(target.coordinator, target.principal);
  } catch (e) {
    toast(isCoordinatorError(e) ? `Coordinator said ${e.code}: ${e.message}` : (e as Error).message);
  } finally {
    ui.busy.delete(key);
    render();
  }
}

function download(): void {
  // A shared run downloads whole, exactly as archived, so its digest can be checked.
  const events = SHARED && player ? player.events : store.state.log;
  const blob = new Blob([JSON.stringify(events, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = ui.run ? `botallion-run-${ui.run.id}.json` : `botallion-${ui.mode}-${(store.state.lastTs ?? "run").replace(/[:.]/g, "")}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

async function copy(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(`${what} copied.`, "info");
  } catch {
    toast(text, "info");
  }
}

/** Live mode: archive what the coordinator holds and show the read-only link. */
async function shareRun(): Promise<void> {
  if (!me() || !repo) return toast("Sign in and choose a repo to share its run.");
  const shown = repo;
  ui.sharing = true;
  render();
  try {
    const when = new Date().toISOString().slice(0, 16).replace("T", " ");
    const { url } = await shareLiveRun({ ...shown, name: `${shown.org}/${shown.repo} run, shared ${when} UTC` });
    ui.shareUrl = url;
    void copy(url, "Read-only replay link");
  } catch (err) {
    toast(`Could not share the run: ${(err as Error).message}`);
  } finally {
    ui.sharing = false;
    render();
  }
}

/** Pause on the previous or next mark of a kind, and show the tab it belongs on. */
function jump(kind: MarkerKind, dir: 1 | -1): void {
  if (!player || !recording) return;
  const target = jumpTarget(marks(), kind, dir, player.position);
  if (!target) return;
  player.pause();
  player.seek(target.offset);
  const marker = recording.markers.find((m) => m.kind === kind && player!.offsetAt(m.index) === target.offset);
  if (marker) {
    ui.view = marker.view;
    ui.lastSwitch = performance.now();
  }
  render();
}

function onDesignLink(link: HTMLAnchorElement): boolean {
  const href = link.getAttribute("href") ?? "";
  if (href === "/onboarding/spec" || href === "#design-spec") {
    ui.step = "spec";
    ui.section = "runs";
    render();
    return true;
  }
  if (href === "/onboarding/repo" || href === "#design-repo") {
    ui.step = "connect";
    ui.section = "runs";
    render();
    return true;
  }
  if (href === "/onboarding/skip" || href === "/invite") return true;
  if (href === "#design-runs" || href === "#design-files" || href === "#design-history" || href === "#design-agents") {
    ui.section = href.slice("#design-".length) as typeof ui.section;
    if (ui.section === "runs") ui.step = "auto";
    render();
    return true;
  }
  return false;
}

function onClick(e: MouseEvent): void {
  const target = e.target as HTMLElement;
  if (useDesign()) {
    const link = target.closest("a");
    if (link && onDesignLink(link)) {
      e.preventDefault();
      return;
    }
  }
  const summary = target.closest("summary");
  const details = summary?.parentElement as HTMLDetailsElement | null;
  if (details?.dataset.overlap) ui.expanded[details.dataset.overlap] = !details.open;
  if (details?.id === "new-run") ui.newRunOpen = !details.open;

  const el = target.closest<HTMLElement>("[data-action],[data-mode],[data-view]");
  if (!el) return;
  if (el.dataset.mode) return switchMode(el.dataset.mode as Mode);
  if (el.dataset.view) {
    ui.view = el.dataset.view as ViewId;
    ui.lastSwitch = performance.now();
    return render();
  }
  const speed = Number(el.dataset.speed);
  switch (el.dataset.action) {
    case "approve-plan": {
      const specId = el.dataset.spec!;
      const plan = store.state.plans[specId];
      if (!plan) return;
      void approve(`plan:${specId}`, (c, approvedBy) => c.approvePlan({ specId, approvedBy, answers: answersFor(ui, plan) }));
      return;
    }
    case "decide-overlap": {
      const overlapId = el.dataset.overlap!;
      const overlap = store.state.overlaps[overlapId];
      if (!overlap) return;
      const [first, other] = el.dataset.pick === "b" ? [overlap.sessionB, overlap.sessionA] : [overlap.sessionA, overlap.sessionB];
      void approve(`overlap:${overlapId}`, (c, decidedBy) =>
        c.recordResolution({
          overlapId,
          outcome: "sequence",
          via: "human",
          agreedBy: [decidedBy],
          reason: "A person decided the order after the agents could not agree.",
          terms: { summary: `${nameOf(store.state, first)} lands first, then ${nameOf(store.state, other)}.`, order: [first, other] },
        }),
      );
      return;
    }
    case "approve-promotion": {
      const deployId = el.dataset.deploy!;
      void approve(`deploy:${deployId}`, (c, approvedBy) => c.approvePromotion({ deployId, approvedBy }));
      return;
    }
    case "mock-toggle":
      if (mockRun?.paused) mockRun.resume();
      else mockRun?.pause();
      return render();
    case "mock-restart":
      return switchMode("mock");
    case "mock-speed":
      ui.speed = speed;
      return render();
    case "replay-toggle":
      if (player?.playing) player.pause();
      else player?.play();
      return;
    case "replay-step":
      return player?.step();
    case "replay-back":
      return player?.stepBack();
    case "jump": {
      const kind = el.dataset.kind as MarkerKind;
      if (MARKER_KINDS.includes(kind)) jump(kind, el.dataset.dir === "-1" ? -1 : 1);
      return;
    }
    case "copy-moment": {
      const url = new URL(location.href);
      url.search = "";
      url.searchParams.set("at", String(store.state.lastSeq));
      void copy(url.toString(), "Link to this moment");
      return;
    }
    case "tl-more":
      $("timeline").classList.toggle("show-more");
      return;
    case "share-run":
      void shareRun();
      return;
    case "open-repo":
      return openRepo();
    case "create-repo":
      void onCreateRepo(el.dataset.template ?? "");
      return;
    case "import-repo":
      // WP-25: the import Worker's page, on this host, for the org chosen here.
      e.preventDefault();
      location.assign(importHref(($("new-org") as HTMLSelectElement | null)?.value ?? ui.home.orgs[0] ?? ""));
      return;
    case "plan-it":
      void onPlanIt();
      return;
    case "offer-agent":
      void onOfferAgent(el.dataset.spec!, el.dataset.wp!);
      return;
    case "copy-text":
      void copy(el.dataset.copy ?? "", "Copied");
      return;
    case "copy-link":
      if (ui.shareUrl) void copy(ui.shareUrl, "Read-only replay link");
      return;
    case "replay-restart":
      player?.seek(0);
      player?.play();
      return;
    case "replay-speed":
      ui.replaySpeed = speed;
      player?.setSpeed(speed);
      return;
    case "download":
      return download();
    case "replay-this": {
      const events = [...store.state.log];
      if (!events.length) return toast("Nothing to replay yet.", "info");
      const from = ui.mode;
      switchMode("replay");
      stopSources();
      startReplay(events, `this ${from} run`);
      return render();
    }
  }
}

function onChange(e: Event): void {
  const el = e.target as HTMLInputElement;
  if (el instanceof HTMLInputElement && el.name === "mode" && (el.value === "create" || el.value === "import")) {
    ui.repoMode = el.value;
    return render();
  }
  if (el.id === "follow") {
    ui.follow = el.checked;
    return render();
  }
  if (el.dataset.action === "replay-speed-select" && player) {
    ui.replaySpeed = Number(el.value);
    player.setSpeed(ui.replaySpeed);
    return;
  }
  if (el.dataset.action === "replay-cap") {
    ui.capGaps = el.checked;
    player?.setGapCap(el.checked ? 2000 : null);
    return;
  }
  if (el.id === "scrub" && player) {
    scrubbing = null;
    player.seek(Number(el.value));
    return;
  }
  if (el.id === "recording" && el.files?.[0]) {
    const file = el.files[0];
    file.text().then(
      (t) => {
        const events = parseRecording(JSON.parse(t));
        stopSources();
        store.reset();
        rendered = {};
        startReplay(events, file.name);
      },
      () => toast("Could not read that file."),
    ).catch((err: Error) => toast(err.message));
  }
}

function onInput(e: Event): void {
  const el = e.target as HTMLTextAreaElement;
  if (el.id === "scrub" && player) {
    scrubbing = Number(el.value);
    if (!scrubFrame) {
      scrubFrame = requestAnimationFrame(() => {
        scrubFrame = 0;
        if (scrubbing !== null && player) player.seek(scrubbing);
        renderProgress();
      });
    }
    return;
  }
  if (el.id === "nm") {
    ui.repoName = el.value;
    return;
  }
  if (el.id === "spec") {
    ui.spec = el.value;
    ui.starter = false;
    if (useDesign()) render();
    return;
  }
  if (el.dataset.answer) {
    ui.answers[el.dataset.answer] = el.value;
    if (useDesign()) render();
    else renderApprovals();
  }
}

/** Ask who is signed in again; live mode connects, or disconnects, to match. */
function signInHref(): string {
  return `${SIGN_IN_PATH}?redirect=${encodeURIComponent(location.pathname + location.search)}`;
}

/** Live mode has no sign-in screen of its own. The accounts page is the sign-in page. */
function goToSignIn(): void {
  location.replace(signInHref());
}

async function refreshAccount(): Promise<void> {
  account = await loadAccount();
  if (ui.mode === "live" && account.status === "signed-out") return goToSignIn();
  if (ui.mode === "live") {
    if (me()) {
      startLive();
      if (!repo) void loadHome();
    } else {
      live?.sub.close();
      live = null;
      ui.stream = "closed";
    }
  }
  render();
}

function onKey(e: KeyboardEvent): void {
  if ((e.target as HTMLElement).closest("input, textarea, select")) {
    if ((e.target as HTMLElement).id === "pick-repo" && e.key === "Enter") openRepo();
    return;
  }
  const n = Number(e.key);
  if (n >= 1 && n <= VIEWS.length) {
    ui.view = VIEWS[n - 1]!;
    ui.lastSwitch = performance.now();
    return render();
  }
  if (e.key === "f") {
    ui.follow = !ui.follow;
    return render();
  }
  if (ui.mode === "replay" && player) {
    if (e.key === " ") {
      e.preventDefault();
      if (player.playing) player.pause();
      else player.play();
    }
    if (e.key === "ArrowRight") player.step();
    if (e.key === "ArrowLeft") player.stepBack();
    const i = REPLAY_SPEEDS.indexOf(player.speed as (typeof REPLAY_SPEEDS)[number]);
    const next = e.key === "+" || e.key === "=" ? REPLAY_SPEEDS[Math.min(REPLAY_SPEEDS.length - 1, i + 1)] : e.key === "-" ? REPLAY_SPEEDS[Math.max(0, i - 1)] : undefined;
    if (next) {
      ui.replaySpeed = next;
      player.setSpeed(next);
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Start
// ---------------------------------------------------------------------------------------------------------------

/** A shared replay: load the archived run and play it. Nothing else on the page can act. */
async function startShared(): Promise<void> {
  ui.mode = "replay";
  ui.follow = true;
  const id = runIdFromPath(location.pathname);
  try {
    if (!id) throw new Error("This is not a run link.");
    const { meta, events } = await loadSharedRun(id);
    ui.run = meta;
    renderRunInfo();
    const at = Number(new URL(location.href).searchParams.get("at"));
    startReplay(events, meta.name, Number.isInteger(at) && at > 0 ? { at } : {});
  } catch (err) {
    put($("runinfo"), "runinfo", html`<div class="run"><strong class="name">No replay here.</strong> <span class="muted">${(err as Error).message}</span></div>`.value);
    toast((err as Error).message);
  }
  render();
}

/**
 * Read-only hooks for the end-to-end tests and anyone checking a replay by hand: the replayed state in the
 * coordinator's snapshot shape, and where the player is. Nothing here can change anything.
 */
function exposeReplay(): void {
  Object.defineProperty(window, "botallionReplay", {
    value: Object.freeze({
      snapshot: () => toSnapshot(store.state),
      applied: () => player?.applied ?? 0,
      position: () => player?.position ?? 0,
      duration: () => player?.duration ?? 0,
    }),
  });
}

async function main(): Promise<void> {
  exposeReplay();
  if (!SHARED) {
    try {
      config = (await (await fetch("/config.json")).json()) as Config;
    } catch {
      // static hosting without the Worker: mock and replay still work
    }
  }
  store.onChange((applied) => {
    follow(applied);
    render();
  });
  document.addEventListener("click", onClick);
  document.addEventListener("change", onChange);
  document.addEventListener("input", onInput);
  document.addEventListener("keydown", onKey);
  wireCopy(document);
  if (SHARED) {
    setInterval(() => {
      renderMain();
      renderChrome();
      renderProgress();
    }, 1000);
    return startShared();
  }
  // Sign-in returns here: the accounts Worker's page sends the browser back to where it came from.
  ($("signin-link") as HTMLAnchorElement).href = signInHref();
  account = await loadAccount();
  const asked = new URL(location.href).searchParams.get("mode");
  const mode: Mode = asked === "mock" || asked === "replay" || asked === "live" ? asked : config.defaultMode;
  if (mode === "live" && account.status === "signed-out") return goToSignIn();
  // A session can end elsewhere (sign out everywhere, a revoke in settings): check again when the tab comes back.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void refreshAccount();
  });
  // Heartbeat ages and the replay position move even when no event arrives.
  setInterval(() => {
    if (useDesign()) {
      renderChrome();
      return;
    }
    renderMain();
    renderChrome();
    renderProgress();
  }, 1000);
  // Costs change when agents and the verifier report, which raises no event: poll them in live mode.
  setInterval(() => void refreshCosts(), 15_000);

  ui.follow = mode !== "live";
  switchMode(mode);
}

void main();
